import React, { useState } from 'react';
import { Modal, Button, Upload, Progress, message } from 'antd';
import { UploadOutlined, InboxOutlined, CheckCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import { UPLOAD_BASE_URL } from '../../utils/constants/config';

const { Dragger } = Upload;

const teal = '#2a5f5a';
const tealHover = '#245249';

export function UploadProjectStudyModal({ open, onClose, onSuccess, projectId, projectName }) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileList, setFileList] = useState([]);

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.warning('Please select files to upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const totalFiles = fileList.length;
      let completed = 0;
      let successCount = 0;
      let failCount = 0;

      // Upload 10 files concurrently. The uploader now runs the Orthanc POST
      // outside its Redis lock so all 10 hit Orthanc simultaneously.
      const CONCURRENCY = 10;

      const uploadOne = async (fileItem) => {
        const file = fileItem.originFileObj || fileItem;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('project_id', projectId);
        try {
          // Use port 8443 (HTTP/1.1) — same bypass as 'Upload Study' to avoid
          // HTTP/2 per-stream flow-control throttling (~430 KB/s on port 443).
          await axios.post(`${UPLOAD_BASE_URL}/uploader/studies/upload`, formData);
          successCount++;
        } catch (err) {
          console.error('Upload error for file:', file.name, err);
          failCount++;
        }
        completed++;
        setUploadProgress(Math.round((completed / totalFiles) * 100));
      };

      for (let i = 0; i < fileList.length; i += CONCURRENCY) {
        await Promise.all(fileList.slice(i, i + CONCURRENCY).map(uploadOne));
      }

      if (failCount === 0) {
        message.success(
          `Study uploaded to ${projectName || 'project'} successfully! (${successCount} file${successCount > 1 ? 's' : ''})`
        );
      } else {
        message.warning(`Uploaded ${successCount} files, ${failCount} failed`);
      }

      // Notify the project study list to refresh immediately (no page reload needed).
      try { window.dispatchEvent(new CustomEvent('project-study-uploaded')); } catch (_) {}

      setTimeout(() => {
        handleClose();
        onSuccess?.();
      }, 1000);
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFileList([]);
    setUploadProgress(0);
    setUploading(false);
    onClose();
  };

  const uploadProps = {
    name: 'file',
    multiple: true,
    accept: '.dcm,.dicom',
    fileList: fileList,
    beforeUpload: (file) => {
      setFileList((prev) => [...prev, file]);
      return false;
    },
    onRemove: (file) => {
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    },
  };

  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 18 }}>
          <UploadOutlined style={{ color: teal, fontSize: 18 }} />
          Upload Study to {projectName || 'Project'}
        </span>
      }
      visible={open}
      onCancel={handleClose}
      width={650}
      footer={[
        <Button key="cancel" onClick={handleClose} disabled={uploading}>
          Cancel
        </Button>,
        <Button
          key="upload"
          type="primary"
          onClick={handleUpload}
          disabled={fileList.length === 0}
          loading={uploading}
          icon={uploading ? <UploadOutlined /> : <CheckCircleOutlined />}
          style={{ background: teal, borderColor: teal }}
        >
          {uploading ? 'Uploading...' : 'Upload Study'}
        </Button>,
      ]}
    >
      <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 14 }}>
        Upload DICOM files to{' '}
        <strong style={{ color: '#111827' }}>{projectName || 'this project'}</strong>.
        The study will be added to this project's data with status{' '}
        <strong style={{ color: '#111827' }}>Unassigned</strong>.
      </p>

      {/* Auto-extraction info banner */}
      <div
        style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 8,
          padding: 16,
          marginBottom: 16,
          fontSize: 14,
          color: '#1e3a5f',
        }}
      >
        <strong>Auto-extraction enabled:</strong> Patient ID, Name, Age, Gender, Modality, and Body Part
        will be automatically extracted from the DICOM file metadata.
      </div>

      {/* File Upload Area */}
      <Dragger {...uploadProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag DICOM files to this area to upload</p>
        <p className="ant-upload-hint">DICOM files only (.dcm, .dicom)</p>
      </Dragger>

      {/* Upload Progress */}
      {uploading && (
        <div style={{ marginTop: 16 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 13,
              marginBottom: 4,
            }}
          >
            <span>Uploading...</span>
            <span style={{ color: teal, fontWeight: 600 }}>{uploadProgress}%</span>
          </div>
          <Progress
            percent={uploadProgress}
            status="active"
            strokeColor={teal}
            showInfo={false}
          />
        </div>
      )}

      {fileList.length > 0 && !uploading && (
        <div style={{ marginTop: 8 }}>
          <strong>Files ready for upload: {fileList.length}</strong>
        </div>
      )}
    </Modal>
  );
}
